import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { useAuth } from "../AuthContext";
import { rootUrl } from "../../../config/config";
import SerialNumbersField, { serialCountError } from "./SerialNumbersField";

const Page = ({ children }) => (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
        <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-xl p-6">{children}</div>
    </div>
);

export default function PartsTransactionPosting() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState("purchase");
    const [vendors, setVendors] = useState([]);
    const [parts, setParts] = useState([]);
    const [currentWeek, setCurrentWeek] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    // Purchase form state
    const [purchaseForm, setPurchaseForm] = useState({
        transaction_type: "PUR",
        vendor_id: "",
        items: [{ part_id: "", quantity: 0, unit_cost: 0, total_cost: 0, serials: [] }],
        invoice_no: "",
        invoice_date: new Date().toISOString().split("T")[0],
        delivery_note_no: "",
    });

    // Transfer form state
    const [transferForm, setTransferForm] = useState({
        transaction_type: "TRF",
        from_location: "",
        to_location: "",
        items: [{ part_id: "", quantity: 0, serials: [] }],
    });

    // Adjustment form state
    const [adjustmentForm, setAdjustmentForm] = useState({
        transaction_type: "ADJ",
        part_id: "",
        quantity_change: 0,
        reason: "",
        serials: [],
    });

    useEffect(() => {
        fetchVendors();
        fetchParts();
        fetchCurrentWeek();
    }, []);

    const fetchVendors = async () => {
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const res = await axios.get(`${rootUrl}/api/vendors/active`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setVendors(res.data || []);
        } catch (err) {
            console.error("Error fetching vendors:", err);
        }
    };

    const fetchParts = async () => {
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const res = await axios.get(`${rootUrl}/car_parts`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setParts(res.data || []);
        } catch (err) {
            console.error("Error fetching parts:", err);
        }
    };

    const fetchCurrentWeek = async () => {
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const res = await axios.get(`${rootUrl}/system/weeks`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const weeks = (res.data?.data || res.data || []).filter(Boolean);
            // Pick the most recent open week
            const open = weeks.find((w) => w.is_closed !== true) || null;
            setCurrentWeek(open);
        } catch (err) {
            console.error("Error fetching current week:", err);
            setCurrentWeek(null);
        }
    };

    const formatMoney = (value) =>
        new Intl.NumberFormat("en-KE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(Number(value || 0));

    const getPartById = (partId) => parts.find((part) => String(part.part_id) === String(partId));

    const getDefinedPartCost = (part) => {
        if (!part) return 0;

        const costIncl = Number(part.cost_incl);
        if (Number.isFinite(costIncl) && costIncl > 0) return costIncl;

        const costExcl = Number(part.cost_excl);
        if (Number.isFinite(costExcl) && costExcl > 0) return costExcl;

        return 0;
    };

    const getPurchaseItemDisplay = (partId) => {
        const part = getPartById(partId);
        if (!part) return "Select a part";

        const prefix = part.part_number ? `${part.part_number} - ` : "";
        return `${prefix}${part.part_name || part.name || "Part"}`;
    };

    const transactionTypeOptions = [
        { value: "purchase", sub_type: "PUR", label: "Purchase (PUR)" },
        { value: "transfer", sub_type: "TRF", label: "Transfer (TRF_OUT/TRF_IN)" },
        { value: "adjustment", sub_type: "ADJ", label: "Adjustment (ADJ)" },
    ];

    // Purchase handlers
    const handlePurchaseChange = (field, value) => {
        setPurchaseForm(prev => ({ ...prev, [field]: value }));
    };

    const handlePurchaseItemChange = (idx, field, value) => {
        setPurchaseForm(prev => {
            const updated = [...prev.items];
            const nextRow = { ...updated[idx], [field]: value };

            if (field === "part_id") {
                const selectedPart = getPartById(value);
                const unitCost = getDefinedPartCost(selectedPart);
                nextRow.serials = [];
                nextRow.unit_cost = unitCost;
                nextRow.total_cost = (Number(nextRow.quantity) || 0) * unitCost;
            }

            if (field === "quantity") {
                nextRow.total_cost = (Number(value) || 0) * (Number(nextRow.unit_cost) || 0);
            }

            if (field === "unit_cost") {
                nextRow.total_cost = (Number(nextRow.quantity) || 0) * (Number(value) || 0);
            }

            updated[idx] = nextRow;
            return { ...prev, items: updated };
        });
    };

    const addPurchaseItem = () => {
        setPurchaseForm(prev => ({
            ...prev,
            items: [...prev.items, { part_id: "", quantity: 0, unit_cost: 0, total_cost: 0, serials: [] }],
        }));
    };

    const removePurchaseItem = (idx) => {
        setPurchaseForm(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== idx),
        }));
    };

    // Transfer handlers
    const handleTransferChange = (field, value) => {
        setTransferForm(prev => ({ ...prev, [field]: value }));
    };

    const handleTransferItemChange = (idx, field, value) => {
        setTransferForm(prev => {
            const updated = [...prev.items];
            updated[idx] = { ...updated[idx], [field]: value, ...(field === "part_id" ? { serials: [] } : {}) };
            return { ...prev, items: updated };
        });
    };

    const addTransferItem = () => {
        setTransferForm(prev => ({
            ...prev,
            items: [...prev.items, { part_id: "", quantity: 0, serials: [] }],
        }));
    };

    const removeTransferItem = (idx) => {
        setTransferForm(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== idx),
        }));
    };

    // Adjustment handlers
    const handleAdjustmentChange = (field, value) => {
        setAdjustmentForm(prev => {
            const directionChanged = field === "quantity_change" && Math.sign(Number(value) || 0) !== Math.sign(Number(prev.quantity_change) || 0);
            const resetSerials = field === "part_id" || directionChanged;
            return { ...prev, [field]: value, ...(resetSerials ? { serials: [] } : {}) };
        });
    };

    const isSerialPart = (partId) => getPartById(partId)?.has_serial === true;
    // Serial tracking switched on before every unit in stock had a serial: no stock movement until they're added
    const serialsMissingError = (partId) => {
        const part = getPartById(partId);
        if (part?.has_serial !== true || part?.serials_pending !== true) return null;
        return `${part.part_name || partId} is blocked until serial numbers are added for the units already in stock. Open the part in Parts, go to its Serial Numbers tab and use "Add missing serials".`;
    };
    const SerialsMissingNote = ({ partId }) => (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">{serialsMissingError(partId)}</p>
    );

    // Returns the first serial validation error across items, or null
    const getItemsSerialError = (items) => {
        for (const item of items) {
            if (!isSerialPart(item.part_id)) continue;
            const blocked = serialsMissingError(item.part_id);
            if (blocked) return blocked;
            const error = serialCountError(item.serials, item.quantity);
            if (error) return `${getPurchaseItemDisplay(item.part_id)}: ${error}`;
        }
        return null;
    };

    // Submit functions
    const submitPurchase = async (e) => {
        e.preventDefault();
        if (!purchaseForm.vendor_id) {
            setMessage({ type: "error", text: "Please select a vendor" });
            return;
        }

        const payloadItems = purchaseForm.items
            .filter((item) => item.part_id)
            .map((item) => {
                const unitCost = Number(item.unit_cost) || 0;
                const quantity = Number(item.quantity) || 0;
                return {
                    ...item,
                    quantity,
                    unit_cost: unitCost,
                    total_cost: quantity * unitCost,
                };
            });

        if (payloadItems.length === 0) {
            setMessage({ type: "error", text: "Please add at least one item" });
            return;
        }
        if (payloadItems.some((item) => Number(item.quantity) <= 0)) {
            setMessage({ type: "error", text: "Each selected part needs a quantity greater than zero" });
            return;
        }
        if (payloadItems.some((item) => Number(item.unit_cost) <= 0)) {
            setMessage({ type: "error", text: "Every selected part must have a defined cost before posting" });
            return;
        }
        const purchaseSerialError = getItemsSerialError(payloadItems);
        if (purchaseSerialError) {
            setMessage({ type: "error", text: purchaseSerialError });
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const payload = {
                transaction_type: purchaseForm.transaction_type,
                vendor_id: purchaseForm.vendor_id,
                items: payloadItems,
                invoice_no: purchaseForm.invoice_no,
                invoice_date: purchaseForm.invoice_date,
                delivery_note_no: purchaseForm.delivery_note_no,
                input_by: user?.username || "system",
            };

            const res = await axios.post(`${rootUrl}/transactions/posting/purchase`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (res.data?.success) {
                setMessage({ type: "success", text: "Parts purchase posted successfully" });
                setPurchaseForm({
                    transaction_type: "PUR",
                    vendor_id: "",
                    items: [{ part_id: "", quantity: 0, unit_cost: 0, total_cost: 0, serials: [] }],
                    invoice_no: "",
                    invoice_date: new Date().toISOString().split("T")[0],
                    delivery_note_no: "",
                });
                clearMessage();
            }
        } catch (err) {
            setMessage({ type: "error", text: err.response?.data?.message || "Failed to post purchase" });
        } finally {
            setLoading(false);
        }
    };

    const submitTransfer = async (e) => {
        e.preventDefault();
        if (!transferForm.from_location || !transferForm.to_location) {
            setMessage({ type: "error", text: "Please select from and to locations" });
            return;
        }
        if (transferForm.items.length === 0 || !transferForm.items[0].part_id) {
            setMessage({ type: "error", text: "Please add at least one item" });
            return;
        }
        const transferSerialError = getItemsSerialError(transferForm.items.filter((item) => item.part_id));
        if (transferSerialError) {
            setMessage({ type: "error", text: transferSerialError });
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const payload = {
                transaction_type: transferForm.transaction_type,
                from_location: transferForm.from_location,
                to_location: transferForm.to_location,
                items: transferForm.items.filter((item) => item.part_id),
                input_by: user?.username || "system",
            };

            const res = await axios.post(`${rootUrl}/transactions/posting/transfer`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (res.data?.success) {
                setMessage({ type: "success", text: "Parts transfer posted successfully" });
                setTransferForm({
                    transaction_type: "TRF",
                    from_location: "",
                    to_location: "",
                    items: [{ part_id: "", quantity: 0, serials: [] }],
                });
                clearMessage();
            }
        } catch (err) {
            setMessage({ type: "error", text: err.response?.data?.message || "Failed to post transfer" });
        } finally {
            setLoading(false);
        }
    };

    const submitAdjustment = async (e) => {
        e.preventDefault();
        if (!adjustmentForm.part_id) {
            setMessage({ type: "error", text: "Please select a part" });
            return;
        }
        if (adjustmentForm.quantity_change === 0 || adjustmentForm.quantity_change === "") {
            setMessage({ type: "error", text: "Quantity change cannot be zero or empty" });
            return;
        }
        if (!adjustmentForm.reason || adjustmentForm.reason.trim() === "") {
            setMessage({ type: "error", text: "Please provide a reason for this adjustment" });
            return;
        }
        if (isSerialPart(adjustmentForm.part_id)) {
            const adjustmentSerialError = serialsMissingError(adjustmentForm.part_id) || serialCountError(adjustmentForm.serials, adjustmentForm.quantity_change);
            if (adjustmentSerialError) {
                setMessage({ type: "error", text: adjustmentSerialError });
                return;
            }
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("authToken") || "default-token";
            const payload = {
                transaction_type: adjustmentForm.transaction_type,
                part_id: adjustmentForm.part_id,
                quantity_change: parseFloat(adjustmentForm.quantity_change),
                reason: adjustmentForm.reason,
                serials: adjustmentForm.serials,
                input_by: user?.username || "system",
            };

            const res = await axios.post(`${rootUrl}/transactions/posting/adjust`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (res.data?.success) {
                setMessage({ type: "success", text: "Parts adjustment posted successfully" });
                setAdjustmentForm({
                    transaction_type: "ADJ",
                    part_id: "",
                    quantity_change: 0,
                    reason: "",
                    serials: []
                });
                clearMessage();
            }
        } catch (err) {
            setMessage({ type: "error", text: err.response?.data?.message || "Failed to post adjustment" });
        } finally {
            setLoading(false);
        }
    };

    // Helper functions
    const getPurchaseTotal = () => {
        return purchaseForm.items.reduce((sum, item) => sum + (item.total_cost || 0), 0);
    };

    const getPartStock = (partId) => {
        const part = parts.find(p => p.part_id === partId);
        return part?.quantity || 0;
    };

    const getTransactionTypeInfo = (type) => {
        const typeMap = {
            PUR: { label: "Purchase", description: "Record a new purchase from vendor (Type: PUR)", color: "blue" },
            TRF: { label: "Transfer", description: "Transfer parts between locations (Type: TRF_OUT/TRF_IN)", color: "purple" },
            ADJ: { label: "Adjustment", description: "Adjust inventory quantities (Type: ADJ)", color: "amber" }
        };
        return typeMap[type] || typeMap.PUR;
    };

    const clearMessage = () => {
        setTimeout(() => {
            setMessage(null);
        }, 3000);
    };

    const vendorOptions = vendors.map((v) => ({ value: v.id, label: v.name || v.vendor_name || `Vendor ${v.id}` }));
    const partOptions = parts.map((p) => ({
        value: p.part_id,
        label: `${p.part_number ? `${p.part_number} - ` : ""}${p.part_name || p.name || "Part"} (Stock: ${p.quantity || 0}, Cost: ${formatMoney(getDefinedPartCost(p))})`,
    }));

    const handleTransactionTypeSelect = (option) => {
        const targetTab = option?.value || "purchase";
        setActiveTab(targetTab);

        if (option?.sub_type === "PUR") {
            setPurchaseForm((prev) => ({ ...prev, transaction_type: "PUR" }));
        }
        if (option?.sub_type === "TRF") {
            setTransferForm((prev) => ({ ...prev, transaction_type: "TRF" }));
        }
        if (option?.sub_type === "ADJ") {
            setAdjustmentForm((prev) => ({ ...prev, transaction_type: "ADJ" }));
        }
    };

    const transactionSelectValue = transactionTypeOptions.find((opt) => opt.value === activeTab);

    return (
        <Page>
            <div className="space-y-6">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-800">Post Parts Transaction</h1>
                    <p className="text-gray-600 mt-2">Record parts purchases, transfers, and adjustments</p>
                </div>

                <div className="rounded-lg p-3 border bg-gray-50 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                        Current Week: {currentWeek?.week ? (
                            <span className="font-semibold text-[#2a2e30]">{currentWeek.week}</span>
                        ) : (
                            <span className="text-red-600 font-semibold">No open week — posting is blocked</span>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={fetchCurrentWeek}
                        className="text-sm px-3 py-1 border rounded-lg hover:bg-gray-100"
                    >
                        Refresh
                    </button>
                </div>

                {message?.text && (
                    <div className={`p-4 rounded-lg ${message.type === "success"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                        }`}>
                        {message.text}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                    <div className="md:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type</label>
                        <Select
                            options={transactionTypeOptions}
                            value={transactionSelectValue}
                            onChange={handleTransactionTypeSelect}
                            classNamePrefix="react-select"
                            styles={{ control: (base) => ({ ...base, borderRadius: 12, borderColor: "#e5e7eb" }) }}
                        />
                    </div>
                    <div className="md:col-span-2 text-sm text-gray-600">
                        {transactionSelectValue?.label}
                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 flex">
                    <button
                        onClick={() => handleTransactionTypeSelect(transactionTypeOptions[0])}
                        className={`px-6 py-3 font-semibold ${activeTab === "purchase"
                            ? "border-b-2 border-[#FF5733] text-[#FF5733]"
                            : "text-gray-600 hover:text-gray-900"
                            }`}
                    >
                        Purchases
                    </button>
                    <button
                        onClick={() => handleTransactionTypeSelect(transactionTypeOptions[1])}
                        className={`px-6 py-3 font-semibold ${activeTab === "transfer"
                            ? "border-b-2 border-[#FF5733] text-[#FF5733]"
                            : "text-gray-600 hover:text-gray-900"
                            }`}
                    >
                        Transfers
                    </button>
                    <button
                        onClick={() => handleTransactionTypeSelect(transactionTypeOptions[2])}
                        className={`px-6 py-3 font-semibold ${activeTab === "adjustment"
                            ? "border-b-2 border-[#FF5733] text-[#FF5733]"
                            : "text-gray-600 hover:text-gray-900"
                            }`}
                    >
                        Adjustments
                    </button>
                </div>

                {/* Purchase Tab */}
                {activeTab === "purchase" && (
                    <form onSubmit={submitPurchase} className="space-y-6">
                        {!vendors.length && (
                            <div className="p-4 bg-yellow-100 text-yellow-800 rounded-lg">
                                No active vendors available. Please add vendors first.
                            </div>
                        )}
                        {!parts.length && (
                            <div className="p-4 bg-yellow-100 text-yellow-800 rounded-lg">
                                No parts available. Please add parts first.
                            </div>
                        )}

                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                            <span className="rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-700">
                                Transaction Type: {getTransactionTypeInfo(purchaseForm.transaction_type).label}
                            </span>
                            <span className="text-gray-400">
                                {getTransactionTypeInfo(purchaseForm.transaction_type).description}
                            </span>
                        </div>

                            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Vendor *</label>
                                    <Select
                                    options={vendorOptions}
                                    value={vendorOptions.find((o) => o.value === purchaseForm.vendor_id) || null}
                                    onChange={(option) => handlePurchaseChange("vendor_id", option?.value || "")}
                                    classNamePrefix="react-select"
                                        isClearable
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Invoice No</label>
                                    <input
                                        type="text"
                                        value={purchaseForm.invoice_no}
                                        onChange={(e) => handlePurchaseChange("invoice_no", e.target.value)}
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-[#FF5733] focus:ring-2 focus:ring-orange-100"
                                        placeholder="Optional"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Date</label>
                                    <input
                                        type="date"
                                        value={purchaseForm.invoice_date}
                                        onChange={(e) => handlePurchaseChange("invoice_date", e.target.value)}
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-[#FF5733] focus:ring-2 focus:ring-orange-100"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">D/Note No</label>
                                    <input
                                        type="text"
                                        value={purchaseForm.delivery_note_no}
                                        onChange={(e) => handlePurchaseChange("delivery_note_no", e.target.value)}
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-[#FF5733] focus:ring-2 focus:ring-orange-100"
                                        placeholder="Optional"
                                    />
                                </div>
                            </div>

                        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                            <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Items</h3>
                                    <p className="text-sm text-gray-600">
                                        Select a defined part and the unit cost is pulled automatically from the product record.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={addPurchaseItem}
                                    className="inline-flex items-center justify-center rounded-lg bg-[#FF5733] px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
                                >
                                    + Add Item
                                </button>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-[980px] w-full border-separate border-spacing-0">
                                    <thead className="bg-gray-50">
                                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                            <th className="border-b border-gray-200 px-4 py-3 w-14">#</th>
                                            <th className="border-b border-gray-200 px-4 py-3">Part</th>
                                            <th className="border-b border-gray-200 px-4 py-3 w-28">Stock</th>
                                            <th className="border-b border-gray-200 px-4 py-3 w-32">Qty</th>
                                            <th className="border-b border-gray-200 px-4 py-3 w-40">Unit Cost</th>
                                            <th className="border-b border-gray-200 px-4 py-3 w-40">Line Total</th>
                                            <th className="border-b border-gray-200 px-4 py-3 w-28">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {purchaseForm.items.map((item, idx) => {
                                            const selectedPart = getPartById(item.part_id);
                                            const partCost = getDefinedPartCost(selectedPart);
                                            return (
                                                <React.Fragment key={idx}>
                                                <tr className="align-top odd:bg-white even:bg-gray-50">
                                                    <td className="border-b border-gray-100 px-4 py-4 text-sm font-semibold text-gray-500">
                                                        {idx + 1}
                                                    </td>
                                                    <td className="border-b border-gray-100 px-4 py-4">
                                                        <Select
                                                            classNamePrefix="react-select"
                                                            options={partOptions}
                                                            value={partOptions.find((o) => o.value === item.part_id) || null}
                                                            onChange={(option) => handlePurchaseItemChange(idx, "part_id", option?.value || "")}
                                                            isClearable
                                                            placeholder="Search part..."
                                                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                            menuPosition="fixed"
                                                            menuShouldScrollIntoView={false}
                                                            styles={{
                                                                control: (base, state) => ({
                                                                    ...base,
                                                                    minHeight: 42,
                                                                    borderRadius: 12,
                                                                    borderColor: state.isFocused ? "#FF5733" : "#d1d5db",
                                                                    boxShadow: "none",
                                                                    "&:hover": { borderColor: "#FF5733" },
                                                                }),
                                                                menu: (base) => ({ ...base, zIndex: 20 }),
                                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                            }}
                                                        />
                                                        <p className="mt-1 text-xs text-gray-500">
                                                            {item.part_id ? getPurchaseItemDisplay(item.part_id) : "Choose a part to load stock and cost"}
                                                        </p>
                                                    </td>
                                                    <td className="border-b border-gray-100 px-4 py-4 text-sm text-gray-700">
                                                        {item.part_id ? (
                                                            <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 font-semibold text-blue-700">
                                                                {getPartStock(item.part_id)}
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    <td className="border-b border-gray-100 px-4 py-4">
                                                        <input
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => handlePurchaseItemChange(idx, "quantity", parseFloat(e.target.value) || 0)}
                                                            min="0"
                                                            step="0.01"
                                                            placeholder="0"
                                                            required={!!item.part_id}
                                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-[#FF5733] focus:ring-2 focus:ring-orange-100"
                                                        />
                                                    </td>
                                                    <td className="border-b border-gray-100 px-4 py-4">
                                                        <input
                                                            type="number"
                                                            value={item.unit_cost}
                                                            onChange={(e) => handlePurchaseItemChange(idx, "unit_cost", parseFloat(e.target.value) || 0)}
                                                            min="0"
                                                            step="0.01"
                                                            placeholder="0.00"
                                                            required={!!item.part_id}
                                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-800 outline-none transition focus:border-[#FF5733] focus:ring-2 focus:ring-orange-100"
                                                        />
                                                        <p className="mt-1 text-xs text-gray-500">VAT-inclusive cost</p>
                                                        {item.part_id && Number(item.unit_cost) !== Number(partCost) && (
                                                            <p className="mt-1 text-xs font-semibold text-orange-700">
                                                                Posting will update the VAT-inclusive master part cost and notify Discord.
                                                            </p>
                                                        )}
                                                    </td>
                                                    <td className="border-b border-gray-100 px-4 py-4">
                                                        <div className="rounded-lg bg-orange-50 px-3 py-2 text-sm font-bold text-[#FF5733]">
                                                            {formatMoney(item.total_cost)}
                                                        </div>
                                                    </td>
                                                    <td className="border-b border-gray-100 px-4 py-4">
                                                        {purchaseForm.items.length > 1 ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => removePurchaseItem(idx)}
                                                                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100"
                                                            >
                                                                Remove
                                                            </button>
                                                        ) : (
                                                            <span className="text-sm text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                                {isSerialPart(item.part_id) && Number(item.quantity) > 0 && (
                                                    <tr>
                                                        <td colSpan={7} className="border-b border-gray-100 px-4 pb-4 bg-orange-50/40">
                                                            {serialsMissingError(item.part_id) ? <SerialsMissingNote partId={item.part_id} /> : (
                                                            <SerialNumbersField
                                                                mode="enter"
                                                                quantity={item.quantity}
                                                                value={item.serials}
                                                                onChange={(serials) => handlePurchaseItemChange(idx, "serials", serials)}
                                                                label="Serial numbers received"
                                                            />
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                                <p className="text-sm text-gray-500">Items added</p>
                                <p className="mt-2 text-2xl font-bold text-gray-900">{purchaseForm.items.filter((i) => i.part_id).length}</p>
                            </div>
                            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                                <p className="text-sm text-gray-500">Total units</p>
                                <p className="mt-2 text-2xl font-bold text-gray-900">
                                    {purchaseForm.items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0).toFixed(2)}
                                </p>
                            </div>
                            <div className="rounded-2xl border border-[#FF5733]/20 bg-orange-50 p-4">
                                <p className="text-sm text-[#a63a24]">Purchase total</p>
                                <p className="mt-2 text-2xl font-bold text-[#FF5733]">
                                    KES {formatMoney(getPurchaseTotal())}
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={loading || !currentWeek?.week}
                                className="px-6 py-2 bg-[#FF5733] text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-400"
                            >
                                {loading ? "Processing..." : "Post Purchase"}
                            </button>
                        </div>
                    </form>
                )}

                {/* Transfer Tab */}
                {activeTab === "transfer" && (
                    <form onSubmit={submitTransfer} className="space-y-6">
                        {!parts.length && (
                            <div className="p-4 bg-yellow-100 text-yellow-800 rounded-lg">
                                No parts available. Please add parts first.
                            </div>
                        )}

                        {/* Transaction Type Info */}
                        <div className={`p-3 rounded-lg border-l-4 bg-purple-50 border-purple-500`}>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-purple-900">
                                    Transaction Type: {getTransactionTypeInfo(transferForm.transaction_type).label}
                                </span>
                            </div>
                            <p className="text-xs text-purple-700 mt-1">
                                {getTransactionTypeInfo(transferForm.transaction_type).description}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">From Location *</label>
                                <input
                                    type="text"
                                    value={transferForm.from_location}
                                    onChange={(e) => handleTransferChange("from_location", e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="e.g., Warehouse A"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">To Location *</label>
                                <input
                                    type="text"
                                    value={transferForm.to_location}
                                    onChange={(e) => handleTransferChange("to_location", e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="e.g., Warehouse B"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Items</h3>
                            <div className="space-y-3">
                                {transferForm.items.map((item, idx) => (
                                    <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end p-3 border rounded-lg bg-gray-50">
                                        <Select
                                            classNamePrefix="react-select"
                                            className="md:col-span-2"
                                            options={partOptions}
                                            value={partOptions.find((o) => o.value === item.part_id) || null}
                                            onChange={(option) => handleTransferItemChange(idx, "part_id", option?.value || "")}
                                            isClearable
                                        />
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => handleTransferItemChange(idx, "quantity", parseFloat(e.target.value) || 0)}
                                            placeholder="Quantity"
                                            min="0"
                                            step="0.01"
                                            required={!!item.part_id}
                                            className="border rounded-lg px-3 py-2"
                                        />
                                        {item.part_id && (
                                            <div className="text-sm text-gray-600">
                                                Stock: <span className="font-semibold">{getPartStock(item.part_id)}</span>
                                            </div>
                                        )}
                                        {transferForm.items.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeTransferItem(idx)}
                                                className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                            >
                                                Remove
                                            </button>
                                        )}
                                        {isSerialPart(item.part_id) && Number(item.quantity) > 0 && (
                                            <div className="md:col-span-5">
                                                {serialsMissingError(item.part_id) ? <SerialsMissingNote partId={item.part_id} /> : (
                                                <SerialNumbersField
                                                    mode="select"
                                                    partId={item.part_id}
                                                    quantity={item.quantity}
                                                    value={item.serials}
                                                    onChange={(serials) => handleTransferItemChange(idx, "serials", serials)}
                                                    label="Serial numbers to transfer"
                                                />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={addTransferItem}
                                className="mt-3 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                            >
                                + Add Item
                            </button>

                            {/* Transfer Summary */}
                            <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                                <h4 className="font-semibold text-gray-800 mb-3">Transfer Summary</h4>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span>Items:</span>
                                        <span className="font-medium">{transferForm.items.filter(i => i.part_id).length}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span>Total Units:</span>
                                        <span className="font-medium">{transferForm.items.reduce((sum, i) => sum + (i.quantity || 0), 0)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={loading || !currentWeek?.week}
                                className="px-6 py-2 bg-[#FF5733] text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-400"
                            >
                                {loading ? "Processing..." : "Post Transfer"}
                            </button>
                        </div>
                    </form>
                )}

                {/* Adjustment Tab */}
                {activeTab === "adjustment" && (
                    <form onSubmit={submitAdjustment} className="space-y-6">
                        {!parts.length && (
                            <div className="p-4 bg-yellow-100 text-yellow-800 rounded-lg">
                                No parts available. Please add parts first.
                            </div>
                        )}

                        {/* Transaction Type Info */}
                        <div className={`p-3 rounded-lg border-l-4 bg-amber-50 border-amber-500`}>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-amber-900">
                                    Transaction Type: {getTransactionTypeInfo(adjustmentForm.transaction_type).label}
                                </span>
                            </div>
                            <p className="text-xs text-amber-700 mt-1">
                                {getTransactionTypeInfo(adjustmentForm.transaction_type).description}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Part *</label>
                                <Select
                                    classNamePrefix="react-select"
                                    options={partOptions}
                                    value={partOptions.find((o) => o.value === adjustmentForm.part_id) || null}
                                    onChange={(option) => handleAdjustmentChange("part_id", option?.value || "")}
                                    isClearable
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Quantity Change *</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        value={adjustmentForm.quantity_change}
                                        onChange={(e) => handleAdjustmentChange("quantity_change", e.target.value)}
                                        className="flex-1 border rounded-lg px-3 py-2"
                                        placeholder="Use + or - value"
                                        step="0.01"
                                        required={!!adjustmentForm.part_id}
                                    />
                                    <div className="text-sm text-gray-600 whitespace-nowrap">
                                        {adjustmentForm.quantity_change > 0 ? (
                                            <span className="text-green-600 font-semibold">+ Add</span>
                                        ) : adjustmentForm.quantity_change < 0 ? (
                                            <span className="text-red-600 font-semibold">- Remove</span>
                                        ) : (
                                            <span className="text-gray-500">No change</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {serialsMissingError(adjustmentForm.part_id) && <SerialsMissingNote partId={adjustmentForm.part_id} />}
                        {isSerialPart(adjustmentForm.part_id) && !serialsMissingError(adjustmentForm.part_id) && Number(adjustmentForm.quantity_change) !== 0 && (
                            <SerialNumbersField
                                key={Number(adjustmentForm.quantity_change) > 0 ? "adjust-in" : "adjust-out"}
                                mode={Number(adjustmentForm.quantity_change) > 0 ? "enter" : "select"}
                                partId={adjustmentForm.part_id}
                                quantity={adjustmentForm.quantity_change}
                                value={adjustmentForm.serials}
                                onChange={(serials) => setAdjustmentForm((prev) => ({ ...prev, serials }))}
                                label={Number(adjustmentForm.quantity_change) > 0 ? "Serial numbers being added" : "Serial numbers being removed"}
                            />
                        )}

                        {adjustmentForm.part_id && (
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="text-sm">
                                    <div className="flex justify-between mb-2">
                                        <span className="text-gray-700">Current Stock:</span>
                                        <span className="font-semibold">{getPartStock(adjustmentForm.part_id)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-700">After Adjustment:</span>
                                        <span className="font-semibold text-[#FF5733]">
                                            {(getPartStock(adjustmentForm.part_id) + (parseFloat(adjustmentForm.quantity_change) || 0)).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Adjustment *</label>
                            <textarea
                                value={adjustmentForm.reason}
                                onChange={(e) => handleAdjustmentChange("reason", e.target.value)}
                                className="w-full border rounded-lg px-3 py-2"
                                rows={4}
                                placeholder="Explain the reason for this adjustment (required for audit trail)"
                                required={true}
                            />
                            <p className="mt-1 text-xs text-gray-500">This reason will be recorded for audit purposes</p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={loading || !currentWeek?.week}
                                className="px-6 py-2 bg-[#FF5733] text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-400"
                            >
                                {loading ? "Processing..." : "Post Adjustment"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </Page>
    );
}
